import { ApiError } from '@/api/client'
import { useChangePassword, useMe } from '@/api/hooks'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { FormError } from '@/components/ui/Feedback'
import { Note } from '@/components/ui/Note'
import { Input } from '@/components/ui/Field'
import { Badge } from '@/components/ui/Badge'
import { PageHeader, PageShell } from '@/components/layout/Page'
import { useDocumentTitle } from '@/lib/useDocumentTitle'
import { KeyRound, ShieldCheck } from 'lucide-react'
import { useState } from 'react'

export function AccountPage() {
  useDocumentTitle('Your account')
  const me = useMe()
  const changePassword = useChangePassword()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')

  const user = me.data
  const mismatch = confirm.length > 0 && confirm !== next
  const tooShort = next.length > 0 && next.length < 10

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    if (mismatch || tooShort) return
    await changePassword.mutateAsync({
      current_password: current,
      new_password: next,
    })
    setCurrent('')
    setNext('')
    setConfirm('')
  }

  const error = changePassword.error
  const message =
    error instanceof ApiError ? error.message : error ? 'Could not change password.' : null

  return (
    <PageShell className="max-w-3xl">
      <PageHeader
        title="Your account"
        subtitle="The recipe collection is shared with everyone who has an account."
      />

      <div className="space-y-4">
        <Card>
          <CardHeader title="Signed in as" />
          <CardBody className="space-y-3">
            <div className="flex items-center gap-3">
              <span
                aria-hidden
                className="grid size-11 shrink-0 place-items-center rounded-full bg-accent-soft text-sm font-semibold text-accent-ink"
              >
                {(user?.display_name || user?.username || '?').charAt(0).toUpperCase()}
              </span>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium text-ink">{user?.display_name}</span>
                  <Badge tone={user?.role === 'admin' ? 'accent' : 'neutral'}>
                    {user?.role === 'admin' ? (
                      <>
                        <ShieldCheck size={11} aria-hidden />
                        Admin
                      </>
                    ) : (
                      'Member'
                    )}
                  </Badge>
                </div>
                <p className="text-xs text-ink-faint">@{user?.username}</p>
              </div>
            </div>
            {user?.must_change_password ? (
              <Note tone="caution">
                You are signed in with a temporary password. Set a new one below before
                continuing.
              </Note>
            ) : null}
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title="Change password"
            subtitle="Other sessions are signed out."
            actions={<KeyRound size={16} aria-hidden className="text-ink-faint" />}
          />
          <CardBody>
            <form onSubmit={submit} className="max-w-sm space-y-3">
              <FormError>{message}</FormError>
              {changePassword.isSuccess && !message ? (
                <Note tone="positive">Password updated.</Note>
              ) : null}
              <Input
                label="Current password"
                type="password"
                value={current}
                onChange={(event) => setCurrent(event.target.value)}
                autoComplete="current-password"
                required
              />
              <Input
                label="New password"
                type="password"
                value={next}
                onChange={(event) => setNext(event.target.value)}
                autoComplete="new-password"
                hint="At least 10 characters."
                error={tooShort ? 'Use at least 10 characters.' : undefined}
                required
              />
              <Input
                label="Repeat new password"
                type="password"
                value={confirm}
                onChange={(event) => setConfirm(event.target.value)}
                autoComplete="new-password"
                error={mismatch ? 'The two passwords do not match.' : undefined}
                required
              />
              <Button
                type="submit"
                variant="primary"
                loading={changePassword.isPending}
                disabled={mismatch || tooShort || !current || !next}
              >
                Update password
              </Button>
            </form>
          </CardBody>
        </Card>
      </div>
    </PageShell>
  )
}
